import React from "react";
import {
  describeProgramProgress,
  ProgramProgress as ProgramProgressModel,
} from "../../utils/programProgress";

// Peak Design System (Figma 2Xgaeen6KRSnprkmMAc2Fy): DaisyUI semantic tokens,
// font-geist for values, font-grotesk for labels, rounded-xl surfaces.

const stateChipClass = (state: ProgramProgressModel["state"]): string => {
  switch (state) {
    case "finished":
      return "bg-success/15 text-success";
    case "notStarted":
      return "bg-base-300/70 text-base-content/60";
    default:
      return "bg-primary/10 text-primary";
  }
};

/** "Week 3 of 8" / "Not started" / "Finished" chip. */
export const ProgramStateChip: React.FC<{
  progress: ProgramProgressModel;
  durationWeeks: number;
  className?: string;
}> = ({ progress, durationWeeks, className = "" }) => (
  <span
    data-testid="program-state"
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide font-grotesk ${stateChipClass(
      progress.state,
    )} ${className}`}
  >
    {progress.state === "inProgress"
      ? `Week ${progress.currentWeek} of ${durationWeeks}`
      : describeProgramProgress(progress)}
  </span>
);

export const ProgramProgressBar: React.FC<{
  progress: ProgramProgressModel;
  className?: string;
}> = ({ progress, className = "" }) => (
  <div className={className}>
    <div className="flex items-center justify-between text-xs font-grotesk text-base-content/60">
      <span>
        <span className="font-geist tabular-nums text-base-content">
          {progress.completed}
        </span>
        /<span className="font-geist tabular-nums">{progress.total}</span>{" "}
        sessions done
      </span>
      <span className="font-geist tabular-nums">{progress.percent}%</span>
    </div>
    <progress
      className="progress progress-primary w-full h-2 mt-1"
      value={progress.percent}
      max={100}
      aria-label={`${progress.completed} of ${progress.total} sessions completed`}
    />
  </div>
);

/** DaisyUI radial progress with completed/total inside. */
export const ProgramProgressRing: React.FC<{
  progress: ProgramProgressModel;
  size?: string;
}> = ({ progress, size = "5.5rem" }) => (
  <div
    className="radial-progress text-primary bg-base-200 border-4 border-base-200"
    style={
      {
        "--value": progress.percent,
        "--size": size,
        "--thickness": "6px",
      } as React.CSSProperties
    }
    role="progressbar"
    aria-valuenow={progress.percent}
    aria-valuemin={0}
    aria-valuemax={100}
    aria-label={`${progress.percent}% of the program complete`}
  >
    <div className="text-center leading-tight">
      <div className="text-lg font-semibold font-geist tabular-nums text-base-content">
        {progress.completed}
        <span className="text-base-content/40">/{progress.total}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wide text-base-content/50 font-grotesk">
        done
      </div>
    </div>
  </div>
);
